/**
 * Global search component with autocomplete
 * Searches across all entities in the system
 *
 * @author Claude
 */
import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  alpha,
  Chip,
  ClickAwayListener,
  Popper,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as ClientIcon,
  Description as ReportIcon,
  Dashboard as DimensionIcon,
  CheckCircle as ProofPointIcon,
  Task as CriteriaIcon,
  DataObject as DataIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useDebounce } from '../../hooks/useDebounce';
import { globalSearch, getSearchSuggestions } from '../../services/searchService';
import { GlobalSearchResponse, SearchResult, SearchSuggestion } from '../../services/searchService';

export interface GlobalSearchProps {
  variant?: 'standard' | 'dialog';
  onClose?: () => void;
}

const getIconForType = (type: string) => {
  switch (type) {
    case 'client':
      return <ClientIcon />;
    case 'report':
      return <ReportIcon />;
    case 'dimension':
      return <DimensionIcon />;
    case 'proofpoint':
      return <ProofPointIcon />;
    case 'criteria':
      return <CriteriaIcon />;
    case 'reportdata':
      return <DataIcon />;
    default:
      return <SearchIcon />;
  }
};

const getColorForType = (type: string, theme: any) => {
  switch (type) {
    case 'client':
      return theme.palette.primary.main;
    case 'report':
      return theme.palette.secondary.main;
    case 'dimension':
      return theme.palette.info.main;
    case 'proofpoint':
      return theme.palette.success.main;
    case 'criteria':
      return theme.palette.warning.main;
    case 'reportdata':
      return theme.palette.error.main;
    default:
      return theme.palette.text.primary;
  }
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ variant = 'standard', onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { token } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const anchorEl = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Handle search
  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (!token || !searchQuery || searchQuery.length < 2) {
        setResults(null);
        setSuggestions([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const searchResults = await globalSearch(token, searchQuery);
        setResults(searchResults);
        setShowResults(true);
      } catch (err: any) {
        console.error('Search failed:', err);
        setError(err.message || 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Handle suggestions
  const handleSuggestions = useCallback(
    async (searchQuery: string) => {
      if (!token || !searchQuery || searchQuery.length < 1) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await getSearchSuggestions(token, searchQuery);
        setSuggestions(response.suggestions);
      } catch (err) {
        console.error('Failed to get suggestions:', err);
        setSuggestions([]);
      }
    },
    [token]
  );

  // Effect for search
  React.useEffect(() => {
    if (debouncedQuery && debouncedQuery.length >= 2) {
      handleSearch(debouncedQuery);
    } else {
      setResults(null);
    }
  }, [debouncedQuery, handleSearch]);

  // Effect for suggestions
  React.useEffect(() => {
    if (query && query.length >= 1 && query.length < 2) {
      handleSuggestions(query);
    } else {
      setSuggestions([]);
    }
  }, [query, handleSuggestions]);

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    setShowResults(false);
    setQuery('');
    navigate(result.url);
    onClose?.();
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.value);
    searchInputRef.current?.focus();
  };

  // Navigate to full search results page
  const handleViewAllResults = useCallback(
    (searchQuery: string) => {
      if (!searchQuery.trim()) return;
      setShowResults(false);
      setQuery('');
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      onClose?.();
    },
    [navigate, onClose]
  );

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults(null);
    setSuggestions([]);
    searchInputRef.current?.focus();
  };

  // Render results
  const renderResults = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!results || results.totalResults === 0) {
      if (suggestions.length > 0 && query.length < 2) {
        return (
          <List dense>
            <ListItem>
              <Typography variant="caption" color="text.secondary">
                Suggestions
              </Typography>
            </ListItem>
            {suggestions.map((suggestion, index) => (
              <ListItemButton key={index} onClick={() => handleSuggestionClick(suggestion)}>
                <ListItemIcon sx={{ minWidth: 36 }}>{getIconForType(suggestion.type)}</ListItemIcon>
                <ListItemText primary={suggestion.value} />
              </ListItemButton>
            ))}
          </List>
        );
      }

      if (debouncedQuery && debouncedQuery.length >= 2) {
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No results found for "{debouncedQuery}"
            </Typography>
          </Box>
        );
      }

      return null;
    }

    const resultGroups = Object.entries(results.results).filter(
      ([_, items]) => items && items.length > 0
    );

    return (
      <List>
        {resultGroups.map(([type, items], groupIndex) => (
          <React.Fragment key={type}>
            {groupIndex > 0 && <Divider />}
            <ListItem>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'capitalize' }}
              >
                {type} ({items.length})
              </Typography>
            </ListItem>
            {items.slice(0, 5).map(item => (
              <ListItemButton
                key={item.id}
                onClick={() => handleResultClick(item)}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(getColorForType(item.type, theme), 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box sx={{ color: getColorForType(item.type, theme) }}>
                    {getIconForType(item.type)}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.description}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Chip
                  label={item.type}
                  size="small"
                  sx={{
                    backgroundColor: alpha(getColorForType(item.type, theme), 0.1),
                    color: getColorForType(item.type, theme),
                  }}
                />
              </ListItemButton>
            ))}
          </React.Fragment>
        ))}
        {results.totalResults > 0 && (
          <>
            <Divider />
            <ListItemButton
              onClick={() => handleViewAllResults(debouncedQuery)}
              sx={{ justifyContent: 'center', py: 1 }}
            >
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                {results.totalResults > 10
                  ? `View all ${results.totalResults} results`
                  : 'View results page'}
              </Typography>
            </ListItemButton>
          </>
        )}
      </List>
    );
  };

  // Standard variant (for header)
  if (variant === 'standard') {
    return (
      <Box ref={anchorEl} sx={{ position: 'relative', width: '100%' }}>
        <TextField
          ref={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search clients, reports, dimensions..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleViewAllResults(query);
            }
          }}
          inputProps={{ 'aria-label': 'Global search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              backgroundColor: alpha(theme.palette.common.white, 0.9),
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 1),
              },
            },
          }}
        />

        <Popper
          open={showResults && (!!results || suggestions.length > 0)}
          anchorEl={anchorEl.current}
          placement="bottom-start"
          transition
          style={{ zIndex: theme.zIndex.modal }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  width: anchorEl.current?.offsetWidth,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <ClickAwayListener onClickAway={() => setShowResults(false)}>
                  <Box>{renderResults()}</Box>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    );
  }

  // Dialog variant (for mobile or focused search)
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '10%',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Search
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search clients, reports, dimensions..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleViewAllResults(query);
            }
          }}
          inputProps={{ 'aria-label': 'Global search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ minHeight: 200, maxHeight: 400, overflow: 'auto' }}>{renderResults()}</Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
